/**
 * 3d-tiles-renderer requests child tiles through global `fetch`, using paths
 * it discovers in tileset JSON. Those URLs are pre-signed FileService URLs
 * (the Splats API `downloadUrl` wildcard and `tilesetUrl`), not Splats API
 * operations, so a generated data-access client cannot issue them. This module
 * rewrites the renderer's fetches onto the signed template.
 */
import { sanitizeTilesetJsonText } from './tileset-json-sanitizer';
import {
  isRequestForTileset,
  isTilesetJsonRequest,
  rewriteTrimbleTileUrl,
  TRIMBLE_HOST,
} from './trimble-tile-url';

async function maybeSanitizeTilesetResponse(
  response: Response,
  url: string,
  tilesetUrl: string
): Promise<Response> {
  if (!response.ok || !isTilesetJsonRequest(url, tilesetUrl)) {
    return response;
  }

  const text = await response.text();
  return new Response(sanitizeTilesetJsonText(text), {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}

interface TileFetchRegistration {
  readonly tilesetUrl: string;
  readonly templateUrl?: string;
}

const tileFetchRegistrations: TileFetchRegistration[] = [];
let nativeFetch: typeof window.fetch | undefined;

function requestUrl(input: RequestInfo | URL): string {
  return typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.href
      : input.url;
}

function findTileFetchRegistration(
  url: string
): TileFetchRegistration | undefined {
  if (!url.includes(TRIMBLE_HOST)) {
    return undefined;
  }

  for (const entry of tileFetchRegistrations) {
    if (isRequestForTileset(url, entry.tilesetUrl)) {
      return entry;
    }
  }

  return undefined;
}

function patchedTileFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const url = requestUrl(input);
  const match = findTileFetchRegistration(url);
  const rewritten = match
    ? rewriteTrimbleTileUrl(url, match.tilesetUrl, match.templateUrl)
    : url;
  const fetchInput =
    rewritten === url
      ? input
      : input instanceof Request
        ? new Request(rewritten, input)
        : rewritten;
  const fetch = nativeFetch ?? window.fetch.bind(window);

  return fetch(fetchInput, init).then((response) =>
    match
      ? maybeSanitizeTilesetResponse(response, rewritten, match.tilesetUrl)
      : response
  );
}

/**
 * Patch global fetch so 3D Tiles child requests get signed flat URLs.
 * Multiple tilesets share one wrapper; uninstall only restores fetch when
 * the last registration is removed.
 */
export function installTrimbleTileFetch(
  tilesetUrl: string,
  templateUrl?: string
): () => void {
  const entry: TileFetchRegistration = { tilesetUrl, templateUrl };
  tileFetchRegistrations.push(entry);

  if (nativeFetch === undefined) {
    nativeFetch = window.fetch.bind(window);
    window.fetch = patchedTileFetch;
  }

  let installed = true;
  return () => {
    if (!installed) {
      return;
    }
    installed = false;

    const index = tileFetchRegistrations.indexOf(entry);
    if (index >= 0) {
      tileFetchRegistrations.splice(index, 1);
    }

    if (tileFetchRegistrations.length === 0 && nativeFetch !== undefined) {
      window.fetch = nativeFetch;
      nativeFetch = undefined;
    }
  };
}
